'use client';
import { ReactNode } from 'react';
import { Sparkles } from 'lucide-react';
import { useTheme } from '@/components/ThemeContext';
import type { AgentConfig } from '@/lib/agent-constants';
import { cn } from '@/lib/utils';

interface AgentPageLayoutProps {
  config: AgentConfig;
  icon: ReactNode;
  isProcessing: boolean;
  aiDone: boolean;
  children: ReactNode;
}

export function AgentPageLayout({ config, icon, isProcessing, aiDone, children }: AgentPageLayoutProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <>
      <main className="min-h-screen pt-16 bg-background text-foreground">
        {/* Agent Header */}
        <section className="relative overflow-hidden border-b border-border">
          <div className={`absolute inset-0 bg-gradient-to-br ${config.gradient.replace('from-', 'from-').split(' ')[0]}/10 via-transparent to-transparent opacity-50`} />
          <div className="w-full px-4 sm:px-6 py-6 relative z-10">
            <div className="flex items-center gap-3">
              <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${config.gradient} flex items-center justify-center text-white shrink-0 shadow-lg`}>
                {icon}
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold tracking-tight">{config.name}</h1>
                <p className="text-xs mt-0.5 text-muted-foreground">{config.subtitle}</p>
              </div>
            </div>
            {/* Workflow Steps */}
            {config.steps.length > 0 && (
              <div className="flex items-center gap-2 mt-4 text-[10px] font-bold">
                {config.steps.map((step, i) => (
                  <div key={i} className="flex items-center gap-1">
                    <span className={`px-2.5 py-1 rounded-lg transition-all ${
                      i === 0 && !isProcessing && !aiDone
                        ? `${config.accentBg} ${config.accentText}`
                        : i === 1 && isProcessing
                        ? `${config.accentBg} ${config.accentText} animate-pulse`
                        : aiDone && i === config.steps.length - 1
                        ? 'bg-emerald-500/20 text-emerald-300'
                        : isDark ? 'bg-white/5 text-gray-600' : 'bg-gray-100 text-gray-400'
                    }`}>{step}</span>
                    {i < config.steps.length - 1 && <span className="text-muted/50">→</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
        {/* Content */}
        <div className="w-full px-4 sm:px-6 py-5">
          {children}
        </div>
      </main>
    </>
  );
}

// ═══ Shared sub-components ═══

interface AgentInputPanelProps {
  children: ReactNode;
  className?: string;
}

export function AgentInputPanel({ children, className = '' }: AgentInputPanelProps) {
  return (
    <div className={cn(
      "rounded-2xl border p-4 space-y-4 bg-card border-border shadow-sm",
      className
    )}>
      {children}
    </div>
  );
}

interface AgentLabelProps {
  children: ReactNode;
}

export function AgentLabel({ children }: AgentLabelProps) {
  return (
    <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
      {children}
    </label>
  );
}

interface AgentStreamOutputProps {
  aiStream: string;
  isProcessing: boolean;
  accentColor?: string;
}

export function AgentStreamOutput({ aiStream, isProcessing, accentColor = 'text-sky-400' }: AgentStreamOutputProps) {
  const { theme } = useTheme();
  if (!aiStream && !isProcessing) return null;
  return (
    <div className="rounded-2xl border p-4 bg-card border-border shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className={`w-4 h-4 ${isProcessing ? `${accentColor} animate-pulse` : 'text-emerald-400'}`} />
        <span className="text-xs font-bold">{isProcessing ? 'AI 분석 중...' : 'AI 분석 완료'}</span>
      </div>
      <pre className="text-[11px] leading-relaxed whitespace-pre-wrap font-mono max-h-[400px] overflow-y-auto text-muted-foreground">
        {aiStream}{isProcessing && <span className="animate-pulse">▊</span>}
      </pre>
    </div>
  );
}

interface AgentEmptyStateProps {
  icon: ReactNode;
  message: string;
}

export function AgentEmptyState({ icon, message }: AgentEmptyStateProps) {
  return (
    <div className="rounded-2xl border border-border p-12 text-center bg-card shadow-sm">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center bg-muted/20">
        {icon}
      </div>
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}
